import React, { useState } from 'react';
import {
  TextField,
  CircularProgress,
  styled,
  Typography,
  Autocomplete,
  useTheme,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/useAutocomplete';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { Fund, useSearchFunds, useGetFunds } from '../api/useFunds';

const Container = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const AutocompleteContainer = styled('div')(({ theme }) => ({
  width: '400px',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.only('xs')]: {
    width: '100%',
  },
}));

const AddIconWithMargin = styled(AddIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
  marginLeft: theme.spacing(1),
}));

const filterOptions = createFilterOptions<Fund>({
  matchFrom: 'any',
  stringify: (option) => `${option.name} ${option.isin} ${option.wkn}`,
});

const Highlight = ({
  parts,
}: {
  parts: {
    text: string;
    highlight: boolean;
  }[];
}) => {
  const theme = useTheme();
  return (
    <>
      {parts.map((part, index) => (
        <span
          key={index}
          style={part.highlight ? { backgroundColor: theme.palette.background.cashback } : {}}
        >
          {part.text}
        </span>
      ))}
    </>
  );
};

interface Props {
  id: string;
  autocompleteKey: string;
  showAddIcon: boolean;
  onChange: (fund: Fund) => void;
}

export const FundAutocomplete = ({ id, autocompleteKey, showAddIcon, onChange }: Props) => {
  const [search, setSearch] = useState<string | undefined>();
  const { t } = useTranslation();
  const { status, data: fundList } = useSearchFunds(search);
  // DE0009848119: DWS Top Dividende LD
  // LU0323578657: Flossbach von Storch SICAV - Multiple Opportunities R
  // DE0009769869: DWS Aktien Strategie Deutschland LC

  const { data: mostUsedFundList } = useGetFunds(['DE0009848119', 'LU0323578657', 'DE0009769869']);

  const handleInputChange = (value: string) => {
    setSearch(value);
  };

  const handleOnChange = (fund: any) => {
    onChange(fund as Fund);
  };

  const getGroup = (isin: string) => {
    switch (isin) {
      case 'DE0009848119':
        return t('cashbackCalculator.detailed.fundAutocomplete.mostUsed');
      case 'LU0323578657':
        return t('cashbackCalculator.detailed.fundAutocomplete.mostUsed');
      case 'DE0009769869':
        return t('cashbackCalculator.detailed.fundAutocomplete.mostUsed');
      default:
        return t('cashbackCalculator.detailed.fundAutocomplete.others');
    }
  };

  return (
    <Container>
      {showAddIcon && <AddIconWithMargin />}
      <AutocompleteContainer>
        <Autocomplete<Fund>
          id={id}
          filterOptions={filterOptions}
          data-testid="fund-auto"
          key={autocompleteKey}
          fullWidth
          loading={status === 'loading'}
          onInputChange={(_event, value) => handleInputChange(value)}
          onChange={(_: any, value: any) => handleOnChange(value)}
          options={fundList?.funds || mostUsedFundList?.funds || []}
          getOptionLabel={(option) => {
            return option.name || '';
          }}
          renderOption={(props, option, { inputValue }) => {
            const matchesName = match(option.name || '', inputValue);
            const partsName = parse(option.name || '', matchesName);
            const matchesIsin = match(option.isin || '', inputValue);
            const partsIsin = parse(option.isin || '', matchesIsin);
            const matchesWkn = match(option?.wkn || '', inputValue);
            const partsWkn = parse(option?.wkn || '', matchesWkn);

            return (
              <li {...props}>
                <Typography data-testid={`fund-autocomplete-option-${option.isin}`}>
                  <strong>
                    <Highlight parts={partsName} />
                  </strong>
                </Typography>
                <Typography variant="caption">
                  ISIN: <Highlight parts={partsIsin} /> WKN: <Highlight parts={partsWkn} />
                </Typography>
              </li>
            );
          }}
          clearOnEscape
          groupBy={(option) => getGroup(option.isin || '')}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('cashbackCalculator.detailed.fundAutocomplete.placeholder')}
              variant="filled"
              inputProps={{
                ...params.inputProps,
                'data-testid': 'fund-autocomplete-input',
                'data-hj-whitelist': true,
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {status === 'loading' ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      </AutocompleteContainer>
    </Container>
  );
};
